import { env } from "./env";
// Adapted from https://github.com/braintree/sanitize-url/blob/main/src/index.ts

//  numeric character references in HTML markup representing an ascii character
//  matching their decimal and hexadecimal values
const htmlEntitiesRegex = /&#(\w+)(^\w|;)?/g;
const protocolAllowList = ["http:", "https:"];

// matching relative URLs that are malformed with multiple forward slashes or backslashes
const unsafeUrlRegex = /^\/(\/|\\)+|^.*?:\/\//i;

// matching ASCII control characters (non-printable characters)
// eslint-disable-next-line no-control-regex
const ctrlCharactersRegex = /[\u0000-\u001F\u007F-\u009F\u2000-\u200D\uFEFF]/gim;
// Not global/multiline on purpose: a /g regex is stateful across .test() calls (lastIndex
// advances), which would make isRelativeUrl's result depend on call history. Only the full match
// ([0]) is used by .match() below, so dropping the flags is behavior-preserving.
const absoluteUrlSchemeRegex = /^([^:]+):/;

const redirectToUrlAllowList = [
  "https://support.sendgrid.com",
  "https://sendgrid.com",
  "https://staging.sendgrid.com",
  env.VITE_MAKO_HOST,
  env.VITE_MC_HOST,
  env.VITE_LOGIN_HOST,
];

export const formRedirectToUrl = (redirectToQueryParam: string) => {
  let redirectToUrl = sanitizeUrl(redirectToQueryParam);
  if (!isAllowListedUrl(redirectToUrl)) {
    return "/";
  }
  return redirectToUrl;
};

export const isRelativeUrl = (url: string) => {
  url = sanitizeUrl(url);
  return !absoluteUrlSchemeRegex.test(url);
};

export const sanitizeUrl = (url: string) => {
  try {
    url = decodeURIComponent(url);
  } catch {
    // decodeURIComponent throws URIError on malformed input (e.g. a bare "%"). Treat an
    // undecodable URL as unsafe rather than letting the exception propagate to callers.
    return "/";
  }
  const sanitizedUrl = decodeHtmlCharacters(url).replace(ctrlCharactersRegex, "").trim();

  if (!sanitizedUrl) {
    return "/";
  }

  const urlSchemeParseResults = sanitizedUrl.match(absoluteUrlSchemeRegex);

  if (!urlSchemeParseResults) {
    // Check for potentially unsafe external links
    if (sanitizedUrl.match(unsafeUrlRegex)) return "/";

    return sanitizedUrl;
  }

  const urlScheme = urlSchemeParseResults[0];

  if (protocolAllowList.indexOf(urlScheme) === -1) {
    return "/";
  }

  return sanitizedUrl;
};

export const isAllowListedUrl = (url: string) => {
  try {
    if (isRelativeUrl(url)) {
      return true;
    }
    return redirectToUrlAllowList.includes(new URL(url).origin);
  } catch {
    return false;
  }
};

const decodeHtmlCharacters = (str: string) => {
  return str.replace(htmlEntitiesRegex, (_, dec) => {
    return String.fromCharCode(dec);
  });
};
